import createFetchival from '@exodus/fetch/create-fetchival'
import { getName as getCountryName } from 'country-list'
import {
  ASSET_TYPES,
  FILTER_LIST_MODES,
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '../constants.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import { filterByCountries, omitUndefinedValues } from '../utils.js'
import { ENABLED_ASSETS, ENABLED_COUNTRIES, ROBINHOOD_API_URL } from './constants.js'
import robinhoodAdapter from './adapter.js'

export * from './constants.js'
export * from './utils.js'
export { default as adapter } from './adapter.js'

const robinhoodProvider = ({
  appId,
  assets: assets_ = ENABLED_ASSETS,
  countries = ENABLED_COUNTRIES,
  countriesMode = FILTER_LIST_MODES.blacklist,
  enabled = true,
  url = ROBINHOOD_API_URL,
  fetch: globalFetch = fetch.bind(globalThis), // eslint-disable-line no-undef
  paymentMethodTypes,
}) => {
  if (!appId) {
    throw new Error('`appId` is required')
  }

  const fetchival = createFetchival({ fetch: globalFetch })
  const fetch_ = fetchival(url)

  const getAssets = async () => {
    const crypto = []
    const fiat = Object.create(null)
    const max = Number.MAX_SAFE_INTEGER
    const min = 0
    const { cryptoCurrencyPairs: providerAssets } = await fetch_(
      'catpay/v1/supported_currencies'
    ).get({ applicationId: appId })

    for (const { assetCurrency, supportedNetworks, quoteCurrency } of providerAssets) {
      const fiatAsset = quoteCurrency.code

      for (const network of supportedNetworks) {
        const asset = robinhoodAdapter.fromProviderToExodusAsset(
          assetCurrency.code,
          network,
          assets_
        )

        if (asset) {
          crypto.push({
            isBuySupported: true,
            isSellSupported: false,
            maxBuyAmount: max,
            minBuyAmount: min,
            name: asset.name,
            type: ASSET_TYPES.crypto,
          })

          fiat[fiatAsset] = {
            isBuySupported: true,
            isSellSupported: false,
            maxBuyAmount: max,
            minBuyAmount: min,
            name: fiatAsset,
            type: ASSET_TYPES.fiat,
          }
        }
      }
    }

    return [...crypto, ...Object.values(fiat)]
  }

  const getCountries = async ({ countryCode, regionCode } = Object.create(null)) =>
    Object.keys(countries)
      .filter(
        (code) =>
          !filterByCountries(countries, code === countryCode ? [countryCode, regionCode] : code, {
            mode: countriesMode,
            properties: [ORDER_TYPES.buy],
          })
      )
      .map((code) => ({
        code,
        isBuySupported: true,
        isSellSupported: false,
        name: getCountryName(code),
      }))

  const getQuote = async (
    {
      countryCode,
      fromAmount,
      fromAsset,
      includeFeeInAmount = true,
      paymentMethod = PAYMENT_METHOD_TYPES.robinhood,
      toAsset,
      toAmount,
      type = ORDER_TYPES.buy,
    } = Object.create(null)
  ) => {
    if (type !== ORDER_TYPES.buy) {
      throw new Error('Only `buy` orders are accepted')
    }

    const providerPaymentMethod = robinhoodAdapter.fromExodusToProviderPaymentMethod(
      paymentMethod,
      {
        assets: assets_,
        country: countryCode,
        currency: fromAsset,
        paymentMethodTypes,
      }
    )

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    const [assetCode, networkCode] = robinhoodAdapter.fromExodusToProviderAsset(toAsset)

    if (!assetCode) {
      throw new Error(`Unsupported asset "${toAsset}"`)
    }

    const quote = await fetch_(`catpay/v1/${encodeURIComponent(assetCode)}/quote`).get(
      omitUndefinedValues({
        applicationId: appId,
        cryptoAmount: toAmount,
        fiatAmount: fromAmount,
        fiatCode: fromAsset,
        includeFeeInAmount,
        networkCode,
        paymentMethod: providerPaymentMethod,
      })
    )

    return robinhoodAdapter.fromProviderToExodusQuote({ ...quote, type })
  }

  const getOrder = async () => {
    throw new Error('Not implemented')
  }

  const getPaymentMethod = (paymentMethod, parameters = Object.create(null)) =>
    robinhoodAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      ...parameters,
      assets: assets_,
      paymentMethodTypes,
    })

  return {
    name: PROVIDERS.robinhood,
    enabled,
    getAssets,
    getCountries,
    getOrder,
    getQuote,
    getPaymentMethod,
    orderTypes: [ORDER_TYPES.buy],
  }
}

export default robinhoodProvider
